// Blind buildings: a building whose row directly south has no walkable tile has nowhere for a shopfront to open onto
// (doors into a wall, signs and light spill facing a neighbour's roof). Those get a plain back wall instead.
// Art only: nothing here touches tiles or collision.
import type { HdBuilding } from '../../common/src/data';
import { buildMap, solidFoot, tileAt, type GameMap } from '../../common/src/map';
import { hash2 } from '../../common/src/util';
import { K, px, dk, alpha } from './hd-kit';
import { registerFront } from './hd-registry';
import { wallAc } from './hd-parts';

export const BLIND_FRONT = 'blindBack';
let cached: GameMap | undefined;

/** true when none of the tiles directly south of the building can be walked on (the map's south edge counts as solid) */
export function isBlind(b: HdBuilding, map: GameMap = (cached ??= buildMap())): boolean {
  for (let x = b.x; x < b.x + b.w; x++) if (!solidFoot(tileAt(map, x, b.y + b.h))) return false;
  return true;
}

/** buildings that already draw a back wall of their own: the map's south-edge service strips and the west border */
export const hasOwnBackWall = (b: HdBuilding, map: GameMap = (cached ??= buildMap())) => b.kind === 'kwestBack' || b.y + b.h >= map.h;

/** true when the generic painter should draw the plain back wall instead of the building's shopfront */
export const needsBackWall = (b: HdBuilding) => isBlind(b) && !hasOwnBackWall(b);

/** back wall: drain pipes, wall units and one plain service door; no sign, no neon, no light */
registerFront(BLIND_FRONT, { wall: K.wallLo, wallAc: true }, ({ g, x, y, w, h, gf, gy, seed }) => {
  for (let i = 18; i < w - 10; i += 56 + Math.floor(hash2(i, seed) * 20)) {
    px(g, x + i, y + 8, 4, h - 12, K.ink); px(g, x + i + 1, y + 8, 1, h - 12, K.metalHi); px(g, x + i + 2, y + 8, 1, h - 12, K.metal);
    for (let j = y + 18; j < y + h - 10; j += 22) px(g, x + i - 1, j, 6, 2, K.metalLo);
  }
  const n = Math.max(1, Math.floor(w / 64));
  for (let i = 0; i < n; i++) {
    const dx = x + Math.round((i + 0.5) * w / n) - 10;
    px(g, dx - 2, gy + 6, 24, gf - 7, K.ink); px(g, dx, gy + 8, 20, gf - 9, i % 2 ? K.metal : K.metalLo); px(g, dx + 15, gy + 22, 2, 4, K.metalHi);
    px(g, dx + 4, gy + 1, 12, 4, K.ink); px(g, dx + 5, gy + 2, 10, 2, dk(K.metalHi, 0.3)); // unlit lamp cage
    if (i % 2) wallAc(g, dx + 28, gy + 10);
  }
  px(g, x + 8, gy + 14, 14, 5, alpha(K.night, 0.3)); // grime
});
